// HeaderRow — THE one-row "item's own row" wrapper (commonization item 3,
// Alex OK 2026-07-20). Every leaf/category page's identity section (Category,
// Family, Line, Product) was the same three-part pattern typed out by hand —
// a title, a <div> wrapper, and a <DataGrid rows={[oneRow]} .../> with a
// matching editRowId pair — six times across CatalogView.jsx and
// ProductsView.jsx. This is the one place that shape lives now.
//
// Also closes the "no title" half of Alex's section-title ask: every header
// row gets a SectionTitle it didn't reliably have before (Catalog's category/
// family/line rows had none at all).
//
//   title      — SectionTitle text ("Category" / "Family" / "Line" / "Product")
//   row        — the single row object (null/undefined => renders nothing)
//   columns    — DataGrid columns for that row
//   onSave     — DataGrid onSave
//   editRowId / onEditRow — edit-mode state (owned by the CALLER — some header
//     rows share the grid's own editRowId on purpose; see CatalogView's line
//     header. HeaderRow never owns this state itself, so that sharing stays
//     possible.)
//   editOnRowClick — default true (click the row to edit, no separate pencil)
//   storageKey — DataGrid column-width persistence key
//   onCopy     — optional copy handler
//   rowLocked  — DataGrid rowLocked (INF-14): a reason string locks the row
//   card       — draw the same card shell as a Spot (border/radius/background,
//                title as its first row). Always OPEN — no chevron, no collapse:
//                this is the item's own identity, which Alex named as one of
//                the surfaces that must never hide (2026-08-20). Opt-in and
//                false by default, so Catalog's Category/Family/Line rows keep
//                today's bare look until asked for.
//   dbId       — the item's `fam:`/`line:`/`prod:` key. Given it, the title row
//                carries the item's PICTURE (Alex, 2026-08-27). May be an array
//                (first hit wins) — a line asks for itself, then its family.
//                Omitted on a CATEGORY header: a folder has no picture.
//   onshapeRef — the picture's fallback source, same contract as the grids'.
//   right      — optional node right-aligned in the title row (SectionTitle's
//                own `right` slot, passed straight through) — e.g. Projects'
//                "Project total" now that it no longer has its own row above
//                this one (DS-5, 2026-08-21: the page-header breadcrumb that
//                used to carry it was removed for being redundant with
//                controls already on screen).

import DataGrid from './DataGrid.jsx';
import SectionTitle from './SectionTitle.jsx';
import ImageCell from './ImageCell.jsx';
import { CARD_STYLE } from '../theme-tokens.js';

export default function HeaderRow({
  title, row, columns, onSave, editRowId, onEditRow,
  editOnRowClick = true, storageKey, onCopy, card = false, right, rowLocked,
  dbId = null, onshapeRef = null,
}) {
  if (!row) return null;
  // ⚠ THE PICTURE APPEARS ONCE, AND THE TITLE WINS. `makeFolderCols` puts an
  // image column on every grid, and a header row IS a one-row grid — so giving
  // the title an icon showed the same picture twice, stacked, on the product
  // page. Dropping it from the row rather than from the title is deliberate:
  // collapsed or scrolled, the title is the half that stays on screen.
  const cols = dbId ? columns.filter(c => c.key !== '_img') : columns;
  return (
    <div style={card ? CARD_STYLE : { marginBottom: 12, flexShrink: 0 }}>
      <SectionTitle
        locked={card}
        right={right}
        icon={dbId ? <ImageCell dbId={dbId} onshapeRef={onshapeRef} size={20} /> : null}
      >{title}</SectionTitle>
      <DataGrid
        rows={[row]}
        columns={cols}
        onSave={onSave}
        onCopy={onCopy}
        onEditRow={onEditRow}
        rowLocked={rowLocked}
        editRowId={editRowId}
        editOnRowClick={editOnRowClick}
        storageKey={storageKey}
      />
    </div>
  );
}
